import { c as _c } from "react/compiler-runtime";
import type { AgentSuggestionOp } from '@onefootprint/request-types/dashboard';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useRef } from 'react';
import { useBuilder } from '../store/provider';
import type { BundleNode } from '../store/types';
import { INPUT_NODE_ID, isDecisionNode, isManagedNode, isRulesNode, isTaskNode, nodeName, OUTPUTS_NODE_ID } from '../store/types';
import { parseChildRowId } from '../store/utils';
type Row = {
  id: string;
  label: string;
  badge?: 'rules' | 'decision';
  contextNodeId?: string;
};
type PlanEntry = {
  kind: 'item';
  row: Row;
};
const suggestionTarget = (operation: AgentSuggestionOp) => {
  if ('nodeId' in operation) return operation.nodeId;
  if (operation.kind === 'update_node') return operation.node.id;
  if (operation.kind === 'add_node') return operation.parentNodeId ?? INPUT_NODE_ID;
  if (operation.kind === 'update_inputs') return INPUT_NODE_ID;
  if (operation.kind === 'update_outputs') return OUTPUTS_NODE_ID;
  return null;
};
const newNodeLabel = (node: BundleNode): string => {
  if (isRulesNode(node)) return 'New rules node';
  if (isDecisionNode(node)) return 'New decision node';
  return 'New task';
};
const nodeBadge = (node: BundleNode): Row['badge'] => {
  if (isRulesNode(node)) return 'rules';
  if (isDecisionNode(node)) return 'decision';
  return undefined;
};
const useNavModel = t0 => {
  const $ = _c(52);
  const enabled = t0 === undefined ? true : t0;
  const {
    state,
    jumpTo
  } = useBuilder();
  const [nodeParam, setNodeParam] = useQueryState("node", parseAsString);
  const consumedFocus = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      kind: "item",
      row: {
        id: INPUT_NODE_ID,
        label: "Input schema"
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== state.bundle.nodes) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        kind: "item",
        row: {
          id: OUTPUTS_NODE_ID,
          label: "Output"
        }
      };
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t2 = [t1, ...state.bundle.nodes.map(_temp), t3];
    $[1] = state.bundle.nodes;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const planEntries = t2;
  const linked = planEntries.find(t3 => {
    const {
      row
    } = t3;
    return row.id === nodeParam || row.contextNodeId === nodeParam;
  })?.row;
  const currentId = linked?.id ?? INPUT_NODE_ID;
  let t4;
  if ($[4] !== jumpTo || $[5] !== planEntries || $[6] !== setNodeParam) {
    t4 = id => {
      const row_0 = planEntries.find(entry => entry.row.id === id)?.row;
      if (!row_0) {
        return;
      }
      setNodeParam(row_0.contextNodeId ?? row_0.id);
      jumpTo(row_0.id);
    };
    $[4] = jumpTo;
    $[5] = planEntries;
    $[6] = setNodeParam;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const select = t4;
  let t5;
  if ($[8] !== enabled || $[9] !== linked || $[10] !== nodeParam || $[11] !== setNodeParam || $[12] !== state.session) {
    t5 = () => {
      if (state.session && enabled && nodeParam && !linked) {
        setNodeParam(null);
      }
    };
    $[8] = enabled;
    $[9] = linked;
    $[10] = nodeParam;
    $[11] = setNodeParam;
    $[12] = state.session;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = linked?.id;
  let t7;
  if ($[14] !== enabled || $[15] !== nodeParam || $[16] !== state.session || $[17] !== t6) {
    t7 = [enabled, nodeParam, t6, state.session];
    $[14] = enabled;
    $[15] = nodeParam;
    $[16] = state.session;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  useEffect(t5, t7);
  let t8;
  if ($[19] !== enabled || $[20] !== planEntries || $[21] !== select || $[22] !== state.focusNodeId) {
    t8 = () => {
      if (!enabled || !state.focusNodeId || consumedFocus.current === state.focusNodeId) {
        return;
      }
      const row_1 = planEntries.find(entry_0 => entry_0.row.contextNodeId === state.focusNodeId)?.row;
      if (!row_1) {
        return;
      }
      consumedFocus.current = state.focusNodeId;
      select(row_1.id);
    };
    $[19] = enabled;
    $[20] = planEntries;
    $[21] = select;
    $[22] = state.focusNodeId;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== enabled || $[25] !== state.bundle.nodes || $[26] !== state.focusNodeId) {
    t9 = [enabled, state.focusNodeId, state.bundle.nodes];
    $[24] = enabled;
    $[25] = state.bundle.nodes;
    $[26] = state.focusNodeId;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  useEffect(t8, t9);
  let t10;
  if ($[28] !== enabled || $[29] !== nodeParam || $[30] !== planEntries || $[31] !== setNodeParam || $[32] !== state.bundle.inputs || $[33] !== state.bundle.nodes || $[34] !== state.bundle.suggestions || $[35] !== state.scrollTarget) {
    t10 = () => {
      if (!enabled || !state.scrollTarget) {
        return;
      }
      const target = state.scrollTarget;
      const child = parseChildRowId(target);
      const owner = child ? state.bundle.nodes.find(node_0 => {
        if (isDecisionNode(node_0)) {
          return child.kind === "field" && node_0.questions.some(q => q.id === child.id);
        }
        const task = isManagedNode(node_0) ? node_0.task : isTaskNode(node_0) ? node_0 : null;
        return task && (child.kind === "field" ? task.fields : task.artifacts).some(item => item.id === child.id);
      }) : undefined;
      const inputOwned = child?.kind === "field" && state.bundle.inputs.some(input => input.id === child.id);
      const suggestion = state.bundle.suggestions.find(s => s.id === target)?.operation;
      const suggestionOwner = suggestion ? suggestionTarget(suggestion) : null;
      const row_3 = planEntries.find(t11 => {
        const {
          row: row_2
        } = t11;
        return row_2.id === target || row_2.contextNodeId === target || inputOwned && row_2.id === INPUT_NODE_ID || owner != null && row_2.contextNodeId === owner.id || suggestionOwner != null && (row_2.contextNodeId === suggestionOwner || row_2.id === suggestionOwner);
      })?.row;
      const targetNode = row_3?.contextNodeId ?? row_3?.id;
      if (targetNode && targetNode !== nodeParam) {
        setNodeParam(targetNode);
      }
    };
    $[28] = enabled;
    $[29] = nodeParam;
    $[30] = planEntries;
    $[31] = setNodeParam;
    $[32] = state.bundle.inputs;
    $[33] = state.bundle.nodes;
    $[34] = state.bundle.suggestions;
    $[35] = state.scrollTarget;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== enabled || $[38] !== nodeParam || $[39] !== state.bundle.inputs || $[40] !== state.bundle.nodes || $[41] !== state.bundle.suggestions || $[42] !== state.scrollTarget) {
    t11 = [enabled, state.scrollTarget, state.bundle.nodes, state.bundle.inputs, state.bundle.suggestions, nodeParam];
    $[37] = enabled;
    $[38] = nodeParam;
    $[39] = state.bundle.inputs;
    $[40] = state.bundle.nodes;
    $[41] = state.bundle.suggestions;
    $[42] = state.scrollTarget;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  useEffect(t10, t11);
  let t12;
  if ($[44] !== currentId || $[45] !== enabled) {
    t12 = id_0 => enabled && currentId === id_0;
    $[44] = currentId;
    $[45] = enabled;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== currentId || $[48] !== planEntries || $[49] !== select || $[50] !== t12) {
    t13 = {
      planEntries,
      currentId,
      isActive: t12,
      jumpTo: select
    };
    $[47] = currentId;
    $[48] = planEntries;
    $[49] = select;
    $[50] = t12;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
};
export type { Row };
export { useNavModel };
function _temp(node) {
  return {
    kind: "item" as const,
    row: {
      id: `${node.kind}-${node.id}`,
      label: nodeName(node) || newNodeLabel(node),
      badge: nodeBadge(node),
      contextNodeId: node.id
    }
  };
}